import { useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { useMutateUserMetadata, useUserMetadata } from '@/hooks/api/useUserMetadata';
import useFirstBuildOnboardingProgress from '@/hooks/useFirstBuildOnboardingProgress';
import { workflowEditorPathWithConfig, workflowEditorTriggersPath } from '@/utils/monolithApiRouteService';
import { shouldRetryQuery } from '@/utils/query';

import { OnboardingStep, OnboardingStepStatus } from './OnboardingChecklist';
import {
  OnboardingChecklistStepId,
  trackChecklistDismissed,
  trackChecklistFinished,
  trackChecklistStepCompleted,
  trackChecklistStepStarted,
} from './onboardingChecklistTracking';

// Persist, per app, that the user finished the completed checklist ("Finish onboarding").
const FINISHED_STORAGE_KEY = 'firstBuildOnboardingFinished';
const DISMISSED_METADATA_KEY = 'first_build_onboarding_checklist_dismissed';
// Persist, per app, which "Step Completed" events have already been sent. Steps complete across a
// full-page navigation to the Workflow Editor (the CTAs are `href` links), so an in-memory flag
// would be lost on remount and the event would never fire — this survives that round-trip.
const STEP_COMPLETED_REPORTED_KEY = 'firstBuildOnboardingStepCompletedReported';
const STEP_COUNT = 3;

type Params = {
  appSlug: string;
  enabled: boolean;
};

type Result = {
  steps: OnboardingStep[];
  /**
   * True once the checklist should stay hidden — completed-and-finished, permanently dismissed, or
   * the dismissal flag could not be read: an unreadable flag is treated as dismissed, so a failing
   * read hands the checklist back to nobody rather than to everyone who already hid it.
   */
  isHidden: boolean;
  isDismissalLoading: boolean;
  finish: () => void;
  /** Permanently hide the checklist ("Don't show again"). */
  dismiss: () => void;
};

const useFirstBuildOnboardingChecklist = ({ appSlug, enabled }: Params): Result => {
  const [finishedRecord, setFinishedRecord] = useLocalStorage<Record<string, boolean>>(FINISHED_STORAGE_KEY, {});
  const isFinished = Boolean(finishedRecord[appSlug]);
  const finish = () => {
    // The completion screen only shows when every step is complete, so completed always equals total.
    trackChecklistFinished({ appSlug, totalSteps: STEP_COUNT, completedSteps: STEP_COUNT });
    setFinishedRecord((prev) => ({ ...prev, [appSlug]: true }));
  };

  const {
    data: dismissedMetadata,
    isLoading: isDismissalLoading,
    isError: isDismissalUnreadable,
  } = useUserMetadata<boolean>(DISMISSED_METADATA_KEY, { enabled });
  const { mutate: mutateUserMetadata } = useMutateUserMetadata({ retry: shouldRetryQuery, survivesNavigation: true });
  const [hasJustDismissed, setHasJustDismissed] = useState(false);
  const isDismissed = Boolean(dismissedMetadata) || hasJustDismissed || isDismissalUnreadable;

  // Only track state while the checklist is actually live for this user.
  const active = enabled && !isFinished && !isDismissed;

  const { progress } = useFirstBuildOnboardingProgress(appSlug);

  const workflowEditorHref = workflowEditorPathWithConfig(appSlug);
  const triggersHref = workflowEditorTriggersPath(appSlug);

  // Step 2: the user visited the Workflow Editor (flag set on the WFE page mount).
  const step2Done = Boolean(progress.openedWorkflowEditor);
  // Step 3: the user edited/added a trigger in the Workflow Editor (WFE TRIGGERS_EDITED event).
  const step3Done = Boolean(progress.triggersEdited);

  // Step 1 (connect_repository) is always complete on a CI page.
  const completedStepsCount = 1 + Number(step2Done) + Number(step3Done);
  const dismiss = () => {
    trackChecklistDismissed({
      appSlug,
      totalSteps: STEP_COUNT,
      completedSteps: completedStepsCount,
      method: 'dont_show_again',
    });
    setHasJustDismissed(true);
    mutateUserMetadata({ [DISMISSED_METADATA_KEY]: true });
  };

  // Fire "Step Completed" once per step. Reported steps are persisted (not just held in memory),
  // so a step that completes during the navigate-to-WFE-and-back flow still fires on the next mount.
  // The pre-completed first step (connect_repository) has no completion event.
  const [stepCompletedReported, setStepCompletedReported] = useLocalStorage<
    Record<string, OnboardingChecklistStepId[]>
  >(STEP_COMPLETED_REPORTED_KEY, {});
  useEffect(() => {
    if (!active) {
      return;
    }
    const reported = stepCompletedReported[appSlug] ?? [];
    const toReport: Array<{ step: OnboardingChecklistStepId; position: number }> = [];
    if (step2Done && !reported.includes('get_to_know_ci_config')) {
      toReport.push({ step: 'get_to_know_ci_config', position: 2 });
    }
    if (step3Done && !reported.includes('automate_builds')) {
      toReport.push({ step: 'automate_builds', position: 3 });
    }
    if (toReport.length === 0) {
      return;
    }
    toReport.forEach(({ step, position }) =>
      trackChecklistStepCompleted({ appSlug, totalSteps: STEP_COUNT, step, stepPosition: position }),
    );
    setStepCompletedReported((prev) => ({
      ...prev,
      [appSlug]: [...(prev[appSlug] ?? []), ...toReport.map(({ step }) => step)],
    }));
  }, [active, step2Done, step3Done, appSlug, stepCompletedReported, setStepCompletedReported]);

  // Step 1 is always complete — being on a CI page means the repo is connected.
  const completions = [true, step2Done, step3Done];
  const activeIndex = completions.indexOf(false);
  const statusFor = (index: number): OnboardingStepStatus => {
    if (completions[index]) {
      return 'completed';
    }
    return index === activeIndex ? 'active' : 'upcoming';
  };

  const steps: OnboardingStep[] = [
    {
      id: 'connect_repository',
      title: 'Connect your repository',
      description: "Your repository is connected — you're all set here.",
      status: statusFor(0),
    },
    {
      id: 'get_to_know_ci_config',
      title: 'Get to know your CI configuration',
      description: 'Edit your Workflow to get a successful build running.',
      status: statusFor(1),
      cta: step2Done
        ? undefined
        : {
            label: 'Edit Workflow',
            href: workflowEditorHref,
            onClick: () =>
              trackChecklistStepStarted({
                appSlug,
                totalSteps: STEP_COUNT,
                step: 'get_to_know_ci_config',
                stepPosition: 2,
              }),
          },
    },
    {
      id: 'automate_builds',
      title: 'Automate your builds',
      description: 'Set up a trigger so Bitrise runs a build on every push.',
      status: statusFor(2),
      cta: step3Done
        ? undefined
        : {
            label: 'Set up a trigger',
            href: triggersHref,
            onClick: () =>
              trackChecklistStepStarted({ appSlug, totalSteps: STEP_COUNT, step: 'automate_builds', stepPosition: 3 }),
          },
    },
  ];

  return {
    steps,
    isHidden: isFinished || isDismissed,
    isDismissalLoading: enabled && isDismissalLoading,
    finish,
    dismiss,
  };
};

export default useFirstBuildOnboardingChecklist;
